import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
export const API = `${BACKEND_URL}/api`;

const ACCESS_TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'rt';

/** Reset on successful login via setAccessToken; caps failed refresh retries per session */
let refreshAttemptCount = 0;

export const encrypt = (text) => {
  try {
    return btoa(encodeURIComponent(text || ''));
  } catch {
    return '';
  }
};

export const decrypt = (encoded) => {
  try {
    return decodeURIComponent(atob(encoded || ''));
  } catch {
    return '';
  }
};

export const getAccessToken = () => (
  localStorage.getItem(ACCESS_TOKEN_KEY) ||
  sessionStorage.getItem(ACCESS_TOKEN_KEY) ||
  ''
);

export const setAccessToken = (token) => {
  if (!token) {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    return;
  }
  // Persist in localStorage so refresh keeps the session (Vercel → Railway Bearer auth).
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
  sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  refreshAttemptCount = 0;
};

export const getRefreshToken = () => {
  const fromEncrypted = decrypt(localStorage.getItem(REFRESH_TOKEN_KEY));
  if (fromEncrypted) return fromEncrypted;
  // Plain storage (legacy / some builds) or key name `refresh_token`
  const plain = localStorage.getItem('refresh_token');
  return (plain && String(plain).trim()) || '';
};

export const setRefreshToken = (token) => {
  if (!token) {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem('refresh_token');
    return;
  }
  localStorage.setItem(REFRESH_TOKEN_KEY, encrypt(token));
  localStorage.removeItem('refresh_token');
};

export const clearAuthStorage = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('refresh_token');
};

export const getCsrfToken = () => {
  const parts = document.cookie.split(';').map((p) => p.trim());
  const match = parts.find((p) => p.startsWith('csrf_token='));
  if (!match) return '';
  return decodeURIComponent(match.split('=').slice(1).join('=') || '');
};

export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  try {
    const payload = refreshToken ? { refresh_token: refreshToken } : {};
    const response = await axios.post(`${API}/auth/refresh`, payload, {
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
    });
    const newToken = response.data.access_token;
    if (newToken) {
      setAccessToken(newToken);
    }
    if (response.data.refresh_token) {
      setRefreshToken(response.data.refresh_token);
    }
    return newToken || null;
  } catch (err) {
    clearAuthStorage();
    return null;
  }
};

/** True if JWT is missing, malformed, or past exp. */
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    if (!exp) return false;
    return exp < Date.now() / 1000;
  } catch {
    return true;
  }
};

/** Refresh before the access token hits exp (reduces 401 storms under load). */
function needsAccessTokenRefresh(token) {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return true;
    const payload = JSON.parse(atob(parts[1]));
    const exp = payload.exp;
    if (!exp) return false;
    return exp < Date.now() / 1000 + 60;
  } catch {
    return true;
  }
}

let proactiveRefreshPromise = null;

async function ensureFreshAccessTokenBeforeRequest(config) {
  const url = String(config.url || '');
  if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
    return;
  }
  const token = getAccessToken();
  if (!token || !needsAccessTokenRefresh(token)) {
    return;
  }
  if (!proactiveRefreshPromise) {
    proactiveRefreshPromise = refreshAccessToken().finally(() => {
      proactiveRefreshPromise = null;
    });
  }
  const newToken = await proactiveRefreshPromise;
  if (!newToken) {
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new Error('Session expired');
  }
}

/**
 * Pre-configured axios instance with auth token injection.
 * Usage:
 *   import api from '@/services/api';
 *   const res = await api.get('/contacts');
 */
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const _bootToken = getAccessToken();
if (_bootToken) {
  api.defaults.headers.common = api.defaults.headers.common || {};
  api.defaults.headers.common.Authorization = `Bearer ${_bootToken}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Global GET caching + request deduplication
// ─────────────────────────────────────────────────────────────────────────────

const apiCache = new Map();
const inFlight = new Map();

const _cacheKeyFor = (url, config) => {
  // Token-scoped so impersonation/admin contexts don't cross-contaminate.
  const token = getAccessToken() || '';
  const paramsKey = config?.params ? JSON.stringify(config.params) : '';
  return `${token}|${url}|${paramsKey}`;
};

/**
 * cachedGet(url, ttl=30000)
 * cachedGet(url, { ttl=30000, params })
 *
 * Returns response.data (not the Axios response).
 * Deduplicates concurrent requests for the same key.
 */
export const cachedGet = async (url, ttl = 30000, config) => {
  let effectiveTtl = ttl;
  let effectiveConfig = config;

  // Backwards-compatible: cachedGet(url, { ttl, params })
  if (ttl && typeof ttl === 'object') {
    effectiveTtl = ttl.ttl ?? 30000;
    effectiveConfig = ttl.params ? { ...(config || {}), params: ttl.params } : (config || {});
  }

  const key = _cacheKeyFor(url, effectiveConfig);
  const cached = apiCache.get(key);
  if (cached && Date.now() - cached.time < effectiveTtl) {
    return cached.data;
  }

  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const promise = api
    .get(url, effectiveConfig)
    .then((res) => {
      apiCache.set(key, { data: res.data, time: Date.now() });
      return res.data;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, promise);
  return promise;
};

export const clearCache = (url) => {
  if (!url) {
    apiCache.clear();
    inFlight.clear();
    return;
  }

  // Best-effort: remove all cache entries that match the URL for any token/params.
  for (const key of Array.from(apiCache.keys())) {
    if (key.includes(`|${url}|`)) apiCache.delete(key);
  }
  for (const key of Array.from(inFlight.keys())) {
    if (key.includes(`|${url}|`)) inFlight.delete(key);
  }
};

// Inject Authorization header; refresh access token before send when expired/near-expiry
api.interceptors.request.use(async (config) => {
  await ensureFreshAccessTokenBeforeRequest(config);
  config.withCredentials = true;
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    api.defaults.headers.common = api.defaults.headers.common || {};
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
  const method = String(config.method || '').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
  }
  return config;
});

// 401 → single-flight refresh (max one attempt per page session), then impersonation recovery, then login
const MAX_REFRESH_ATTEMPTS = 1;
let refreshInFlight = null;

function runSingleFlightRefresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      if (refreshAttemptCount >= MAX_REFRESH_ATTEMPTS) {
        return null;
      }
      refreshAttemptCount += 1;
      const token = await refreshAccessToken();
      if (token) {
        refreshAttemptCount = 0;
      }
      return token;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retry) {
      const url = String(original.url || '');
      if (url.includes('/auth/refresh') || url.includes('/auth/login')) {
        return Promise.reject(error);
      }
      original._retry = true;
      const impersonation = localStorage.getItem('impersonation');
      if (impersonation) {
        const adminToken = localStorage.getItem('admin_token');
        localStorage.removeItem('impersonation');
        localStorage.removeItem('admin_token');
        if (adminToken) {
          setAccessToken(adminToken);
          window.location.href = '/admin/demos';
          return Promise.reject(error);
        }
      }
      const newToken = await runSingleFlightRefresh();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      clearAuthStorage();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
